import { Component, ChangeDetectionStrategy, computed, inject, input } from "@angular/core";
import { RouterModule } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { TooltipModule } from "primeng/tooltip";
import { RippleModule } from "primeng/ripple";

import { UserContext } from "src/app/store/user/user.state";
import { selectCustomerCode, selectCustomerName } from "src/app/store/pages/customer/customer.selectors";
import { NavRoutesService } from "src/app/navigation/nav-routes.service";
import { NavContextHeaderComponent } from "../../parts/nav-context-header/nav-context-header.component";

@Component({
    selector: "app-customer-view",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterModule, TranslateModule, TooltipModule, RippleModule, NavContextHeaderComponent],
    templateUrl: "./customer-view.component.html",
    styleUrl: "./customer-view.component.scss",
})
export class CustomerViewComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly navRoutes = inject(NavRoutesService);

    // =========================================================================
    // INPUTS
    // =========================================================================
    public readonly expanded = input.required<boolean>();
    public readonly user = input.required<UserContext | null>();

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly customerId = toSignal(this.store.select(selectCustomerCode), { initialValue: null });
    private readonly customerName = toSignal(this.store.select(selectCustomerName), { initialValue: null });

    // =========================================================================
    // COMPUTED - Routes
    // =========================================================================
    public readonly overviewRoute = computed(() => this.tabRoute("overview"));

    // =========================================================================
    // COMPUTED - Header
    // =========================================================================
    public readonly customersListRoute = computed(() => this.navRoutes.route("customer"));
    public readonly headerTitle = computed(() => this.customerName() ?? this.customerId() ?? "");
    public readonly headerLoading = computed(() => !this.customerName() && !this.customerId());

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private tabRoute(tab: string): string[] | null {
        const cId = this.customerId();
        return cId ? this.navRoutes.route("customer", cId, tab) : null;
    }
}
